import { makeAutoObservable } from 'mobx';

/**
 * UserAuthStore - Manages JWT-based user authentication
 * Separate from Tradier OAuth login (tradierLoginStore)
 */
export class UserAuthStore {
  isAuthenticated = false;
  username = null;
  token = null;
  loginError = null;
  isLoggingIn = false;

  constructor(rootStore) {
    makeAutoObservable(this);
    this.rootStore = rootStore;

    // Check for existing token on initialization
    this.initializeFromStorage();
  }

  initializeFromStorage = () => {
    const storedToken = localStorage.getItem('jwt_token');
    const storedUsername = localStorage.getItem('username');

    if (storedToken && storedUsername) {
      this.token = storedToken;
      this.username = storedUsername;
      this.isAuthenticated = true;
    }
  };

  login = async (username, password) => {
    this.isLoggingIn = true;
    this.loginError = null;

    try {
      const baseUrl = window.config?.REACT_APP_REDIRECT || 'http://localhost:8080/';

      const response = await fetch(`${baseUrl}api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Important: allows cookies
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Invalid username or password');
        }
        throw new Error('Login failed. Please try again.');
      }

      const data = await response.json();
      const { token, username: user } = data;

      // Store token and username
      localStorage.setItem('jwt_token', token);
      localStorage.setItem('username', user);

      this.token = token;
      this.username = user;
      this.isAuthenticated = true;
      this.loginError = null;

      return true;
    } catch (error) {
      this.loginError = error.message;
      this.isAuthenticated = false;
      return false;
    } finally {
      this.isLoggingIn = false;
    }
  };

  logout = async () => {
    try {
      const baseUrl = window.config?.REACT_APP_REDIRECT || 'http://localhost:8080/';

      await fetch(`${baseUrl}api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      // Clear local state regardless of API call success
      this.clearAuth();
    }
  };

  clearAuth = () => {
    localStorage.removeItem('jwt_token');
    localStorage.removeItem('username');
    this.token = null;
    this.username = null;
    this.isAuthenticated = false;
    this.loginError = null;
  };

  changePassword = async (oldPassword, newPassword) => {
    try {
      const baseUrl = window.config?.REACT_APP_REDIRECT || 'http://localhost:8080/';

      const response = await fetch(`${baseUrl}api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.token}`,
        },
        credentials: 'include',
        body: JSON.stringify({
          username: this.username,
          oldPassword,
          newPassword,
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Invalid old password');
        }
        throw new Error('Failed to change password. Please try again.');
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };
}
